<script lang="ts" setup>
import {nextTick, onMounted, ref, computed} from 'vue'
import SendIcon from "@/assets/widgetIcons/SendIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {QInput} from "quasar";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

type AttachmentStatus = 'uploading' | 'ready' | 'error';

interface ChatAttachment {
  fileName: string;
  mimeType: string;
  size: number;
  status: AttachmentStatus;
  fileId?: string;
  providerName?: string;
  errorMessage?: string;
}

const props = defineProps<{
  chatId?: string;
  selectedChatModel?: string;
}>();

const message = ref('')
const attachment = ref<ChatAttachment | null>(null)

const emit = defineEmits<{
  (e: 'send', message: string, attachment: ChatAttachment | null): void
}>()
const inputRef = ref<QInput>();
const fileInputRef = ref<HTMLInputElement>();

const canSend = computed(() => {
  const hasText = !!message.value.trim();
  const hasReadyFile = !!attachment.value && attachment.value.status === 'ready';
  const isUploading = !!attachment.value && attachment.value.status === 'uploading';
  return (hasText || hasReadyFile) && !isUploading;
});

function sendMessage() {
  if (!canSend.value) return;
  emit('send', message.value, attachment.value)
  message.value = ''
  attachment.value = null
  nextTick(() => {
    if (inputRef.value?.$el) {
      const textarea = inputRef.value.$el.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }
  });
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
    event.preventDefault();
    sendMessage();
  }
}

function openFilePicker() {
  if (!props.selectedChatModel) {
    useErrorsStore().pushErrorStr(Translator.translate('aiPickModelBeforeAttach'));
    return;
  }
  fileInputRef.value?.click();
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  if (file.size > MAX_ATTACHMENT_BYTES) {
    useErrorsStore().pushErrorStr(Translator.translate('aiAttachmentTooLarge'));
    return;
  }

  attachment.value = {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    status: 'uploading'
  };

  try {
    const base64Content = await readFileAsBase64(file);
    const resp = await NamaCommon.namaFetch(NamaURLS.UPLOAD_AI_FILE, {
      data: NamaCommon.stringify({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64Content,
        chatId: props.chatId,
        selectedChatModel: props.selectedChatModel
      }),
      noLoadingIndicator: true
    });
    if (!resp || !resp.fileId) {
      throw new Error('Upload failed: no fileId returned');
    }
    if (attachment.value && attachment.value.fileName === file.name) {
      attachment.value = {
        ...attachment.value,
        status: 'ready',
        fileId: resp.fileId,
        providerName: resp.providerName
      };
    }
  } catch (e: any) {
    console.error('Failed to upload attachment', e);
    if (attachment.value && attachment.value.fileName === file.name) {
      attachment.value = {
        ...attachment.value,
        status: 'error',
        errorMessage: e?.message || 'Upload failed'
      };
    }
    useErrorsStore().pushErrorStr(Translator.translate('aiAttachmentUploadFailed'));
  }
}

function removeAttachment() {
  attachment.value = null;
}

function formatSize(size: number): string {
  if (size < 1024) return size + ' B';
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
  return (size / (1024 * 1024)).toFixed(1) + ' MB';
}

onMounted(() => {
  nextTick(() => {
    inputRef.value?.focus();
  });
})

</script>


<template>
  <div class="tw-flex tw-flex-col tw-rounded-2xl tw-p-2 tw-bg-transparent tw-w-full">
    <input
      ref="fileInputRef"
      type="file"
      style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0"
      accept=".pdf,.txt,.md,.csv,.json,.xml,.html,.htm,.log,.yaml,.yml,.properties,.png,.jpg,.jpeg,.gif,.webp,text/*,application/pdf,application/json,application/xml,image/*"
      @change="onFileSelected"/>
    <transition name="nama-chat-attachment">
      <div v-if="attachment"
           class="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-px-2 tw-py-1 tw-rounded-lg tw-w-fit tw-max-w-full"
           :class="{
             'tw-bg-gray-100 dark:tw-bg-gray-700': attachment.status !== 'error',
             'tw-bg-red-100 dark:tw-bg-red-900': attachment.status === 'error'
           }">
        <q-spinner v-if="attachment.status === 'uploading'" size="sm" color="primary"/>
        <q-icon v-else-if="attachment.status === 'ready'" name="check_circle" size="sm" class="tw-text-green-600"/>
        <q-icon v-else name="error" size="sm" class="tw-text-red-600"/>
        <q-icon name="description" size="sm" class="tw-text-primary"/>
        <span class="tw-text-sm tw-truncate tw-max-w-[220px] tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
              :title="attachment.fileName">{{ attachment.fileName }}</span>
        <span class="tw-text-xs tw-text-gray-500">{{ formatSize(attachment.size) }}</span>
        <span v-if="attachment.status === 'uploading'" class="tw-text-xs tw-text-gray-500">{{ Translator.translate('aiAttachmentUploading') }}</span>
        <span v-else-if="attachment.status === 'error'" class="tw-text-xs tw-text-red-700 dark:tw-text-red-300" :title="attachment.errorMessage">{{ Translator.translate('aiAttachmentUploadFailed') }}</span>
        <q-btn dense flat round size="xs" icon="close" :title="Translator.translate('aiRemoveAttachment')" @click="removeAttachment"/>
      </div>
    </transition>
    <div class="tw-flex tw-items-end tw-w-full">
      <q-input
        ref="inputRef"
        type="textarea"
        outlined
        v-model="message"
        @keydown="handleKeyDown"
        autogrow
        :input-style="{ minHeight: '95%', maxHeight: '95%', resize: 'none' }"
        input-class="focus:tw-ring-transparent focus:tw-ring-0 tw-bg-transparent tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
        class="nama-field-chat-input tw-flex-1 focus:tw-outline-none focus-visible:tw-outline-none">
        <template v-slot:prepend>
          <div class="tw-flex tw-items-end tw-pb-1">
            <q-btn
              flat
              round
              dense
              icon="add"
              :title="Translator.translate('aiAttachFile')"
              @click.stop.prevent="openFilePicker"
              class="tw-p-1 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"/>
          </div>
        </template>
        <template v-slot:append>
          <div class="tw-flex tw-items-end tw-pb-1">
            <q-btn
              flat
              round
              dense
              @click="sendMessage"
              :title="Translator.translate('send')"
              :disabled="!canSend"
              class="tw-p-1 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700">
              <SendIcon class="tw-size-5"
                        :class="[canSend ? 'tw-text-primary' : 'tw-text-gray-400',{'tw-rotate-180':Translator.isLanguageRTL()}]"/>
            </q-btn>
          </div>
        </template>
      </q-input>
    </div>
  </div>
</template>

<style>
.nama-field-chat-input.q-field--outlined .q-field__control:before {
  @apply tw-bg-transparent tw-rounded-xl tw-border dark:tw-border-white
}

.nama-field-chat-input .q-field__control {
  @apply tw-rounded-xl
}

.nama-field-chat-input textarea {
  @apply tw-py-2
}

.nama-chat-attachment-enter-active,
.nama-chat-attachment-leave-active {
  transition: opacity 120ms ease, transform 120ms ease;
}

.nama-chat-attachment-enter-from,
.nama-chat-attachment-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
